import { useState } from 'react';
import { Check, Play } from 'lucide-react';
import { CASE_STUDY_DISCLAIMER } from '../brand';
import { projects } from '../content';
import type { DemoId } from '../demos/demoDefinitions';
import { WorkflowPreview } from './WorkflowPreview';

type Project = typeof projects[keyof typeof projects];

type WorkCase = {
  number: string;
  label: string;
  scope: string;
  demoId: DemoId;
  project: Project;
};

const cases = [
  { number: '01', label: 'Portfolio operations', scope: 'Custom app / permissions / AI', demoId: 'portfolio-operations', project: projects.portfolioCommand },
  { number: '02', label: 'Routine companion', scope: 'Mobile app / schedules', demoId: 'routine-wellness', project: projects.healthTracker },
  { number: '03', label: 'Inventory intelligence', scope: 'Imports / dashboards / actions', demoId: 'inventory-sales', project: projects.automotiveOperations },
  { number: '04', label: 'Connected commerce', scope: 'Operations / web / data', demoId: 'commerce-brand', project: projects.connectedBrandOperations },
  { number: '05', label: 'Shared reporting', scope: 'Multi-company / controls', demoId: 'cross-company-reporting', project: projects.sharedCompanyOperations },
] as const satisfies readonly WorkCase[];

export function Work() {
  const [activeIndex, setActiveIndex] = useState(0);
  const active = cases[activeIndex];

  const moveTab = (index: number) => {
    const next = (index + cases.length) % cases.length;
    setActiveIndex(next);
    window.requestAnimationFrame(() => document.getElementById(`work-tab-${cases[next].number}`)?.focus());
  };

  return (
    <section className="work" id="work">
      <div className="work__shell section-shell">
        <header className="work__header reveal">
          <div><span className="section-kicker">Selected work</span><h2>See the system, not a sales slide.</h2></div>
          <p>Choose a workflow to watch a real interface move through a representative task.</p>
        </header>

        <div className="work-browser reveal">
          <div className="work-tabs" role="tablist" aria-label="Selected systems">
            {cases.map((workCase, index) => (
              <button
                key={workCase.number}
                id={`work-tab-${workCase.number}`}
                type="button"
                role="tab"
                aria-selected={index === activeIndex}
                aria-controls="work-panel"
                tabIndex={index === activeIndex ? 0 : -1}
                onClick={() => setActiveIndex(index)}
                onKeyDown={(event) => {
                  if (event.key === 'ArrowRight' || event.key === 'ArrowDown') { event.preventDefault(); moveTab(index + 1); }
                  if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') { event.preventDefault(); moveTab(index - 1); }
                  if (event.key === 'Home') { event.preventDefault(); moveTab(0); }
                  if (event.key === 'End') { event.preventDefault(); moveTab(cases.length - 1); }
                }}
              >
                <span>{workCase.number}</span>
                <strong>{workCase.label}</strong>
                <small>{workCase.scope}</small>
                {index === activeIndex && <Play aria-hidden="true" />}
              </button>
            ))}
          </div>

          <article
            className="work-panel"
            id="work-panel"
            role="tabpanel"
            aria-labelledby={`work-tab-${active.number}`}
          >
            <WorkflowPreview key={active.demoId} demoId={active.demoId} />
            <div className="work-panel__copy">
              <div><span>{active.scope}</span><h3>{active.project.title}</h3><p>{active.project.summary}</p></div>
              <ul aria-label="System capabilities">{active.project.details.map((detail) => <li key={detail}><Check aria-hidden="true" />{detail}</li>)}</ul>
            </div>
          </article>
        </div>

        <p className="representative-note">{CASE_STUDY_DISCLAIMER}</p>
      </div>
    </section>
  );
}
